import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import { debounce } from 'lodash';
import * as React from 'react';

type GenericSearchType = {
  id: string;
  title: string;
  subtitle?: string;
  data?: any;
};

export type SearchSupportedAutocompleteProps = {
  label: string;
  search: (searchValue: string) => Promise<GenericSearchType[]>;
  onChange?: (value: GenericSearchType | null) => void;
  value?: GenericSearchType | null;
  placeholder?: string;
};

/**
 * A React component that provides an autocomplete input with search functionality, so it can be used as a typeahead input.
 * This component allows users to search for options and select one from the list.
 * @param label - The label for the autocomplete input
 * @param search - A function that takes a search value and returns a promise that resolves to an array of options
 * @param onChange - A callback function that is called when the selected value changes
 * @param value - The currently selected value
 * @param placeholder - The placeholder text for the input
 * @returns SearchSupportedAutocomplete component
 */
export const SearchSupportedAutocomplete: React.FC<
  SearchSupportedAutocompleteProps
> = ({
  label,
  search,
  onChange = () => null,
  value = null,
  placeholder = 'Search...',
}) => {
  const [options, setOptions] = React.useState<readonly GenericSearchType[]>(
    [],
  );
  const [loading, setLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [selectedValue, setSelectedValue] =
    React.useState<GenericSearchType | null>(value);

  // Add a debounced search handler, that will be called when the input value changes
  const handleSearch = React.useCallback(
    debounce(
      (searchValue: string) => {
        if (!searchValue) {
          return Promise.resolve([]);
        }

        setLoading(true);
        return search(searchValue)
          .then((newOptions) => setOptions(newOptions))
          .finally(() => setLoading(false));
      },
      300,
      {
        leading: true,
        trailing: true,
      },
    ),
    [],
  );

  // Call the search handler when the input value changes
  React.useEffect(() => {
    handleSearch(inputValue);
  }, [inputValue]);

  return (
    <Autocomplete
      fullWidth
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.title}
      options={options}
      loading={loading}
      value={selectedValue}
      noOptionsText="Type to search"
      filterOptions={(x) => x} // Disable default filtering
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(_event, newValue) => {
        setSelectedValue(newValue);
        onChange(newValue);
      }}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText
            slotProps={{
              primary: {
                sx: {
                  textTransform: 'capitalize',
                },
              },
            }}
            primary={option.title}
            secondary={option?.subtitle}
          />
        </ListItem>
      )}
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          sx={{ textTransform: 'capitalize' }}
          slotProps={{
            input: {
              ...params.InputProps,
              sx: {
                '& input': {
                  textTransform: 'capitalize',
                },
              },
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
};
